// LRU caches for image bytes fetched as authenticated blobs, keyed by Drive file id.
// Kept separate from React Query: blobs need explicit memory management (revoking
// object URLs) that a generic query cache doesn't do, and full-res images vs.
// thumbnails warrant very different capacities.

interface CacheEntry {
    objectUrl: string;
    blob: Blob;
}

function createBlobCache(maxEntries: number) {
    const cache = new Map<string, CacheEntry>();
    const inFlight = new Map<string, Promise<CacheEntry>>();

    function touch(key: string, entry: CacheEntry): void {
        cache.delete(key);
        cache.set(key, entry);
        while (cache.size > maxEntries) {
            const oldestKey = cache.keys().next().value;
            if (oldestKey === undefined) break;
            const oldest = cache.get(oldestKey);
            if (oldest) URL.revokeObjectURL(oldest.objectUrl);
            cache.delete(oldestKey);
        }
    }

    function getCached(key: string): CacheEntry | undefined {
        const entry = cache.get(key);
        if (entry) touch(key, entry);
        return entry;
    }

    /** Loads (or returns the cached) object URL for a key, deduplicating concurrent requests. */
    function load(key: string, fetchBlob: () => Promise<Blob>): Promise<CacheEntry> {
        const cached = getCached(key);
        if (cached) return Promise.resolve(cached);

        const existing = inFlight.get(key);
        if (existing) return existing;

        const promise = fetchBlob()
            .then((blob) => {
                const entry: CacheEntry = {blob, objectUrl: URL.createObjectURL(blob)};
                touch(key, entry);
                inFlight.delete(key);
                return entry;
            })
            .catch((err) => {
                inFlight.delete(key);
                throw err;
            });
        inFlight.set(key, promise);
        return promise;
    }

    /** Fire-and-forget warm-up; failures are silent (a real error surfaces if the user actually needs it). */
    function prefetch(key: string, fetchBlob: () => Promise<Blob>): void {
        if (cache.has(key) || inFlight.has(key)) return;
        load(key, fetchBlob).catch(() => undefined);
    }

    /** The cached entry without marking it as recently used — safe to call while rendering. */
    function peek(key: string): CacheEntry | undefined {
        return cache.get(key);
    }

    return {getCached, peek, load, prefetch};
}

// Full-resolution viewer images: few, but large — small cap.
const imageCache = createBlobCache(20);
export const getCachedImage = imageCache.getCached;
export const peekCachedImage = imageCache.peek;
export const loadImage = imageCache.load;
export const prefetchImage = imageCache.prefetch;

// Grid thumbnails (extracted from a small byte-range fetch, not full files): many, but
// small — much bigger cap so scrolling a large album doesn't keep re-fetching thumbnails
// that scrolled out of view and back in.
const thumbnailCache = createBlobCache(300);
export const getCachedThumbnail = thumbnailCache.getCached;
export const loadThumbnail = thumbnailCache.load;
